import { Box } from "@mui/material"
import type { FC } from "react"
import type { Control, UseFormWatch } from "react-hook-form"

import type { CarrierPreferencesData } from "../types/carrierPreferences"

import { FormAutocomplete, FormBooleanRadioGroup, FormSelect, FormTextField } from "@/components/UiKit/FormControls"
import { FREIGHT_TERMS_OPTIONS } from "@/constants/freighTerms"
import { CarrierType, type CarrierType as CarrierTypeEnum, type Company } from "@/graphql/codegen/graphql"

type CompanyBase = Pick<Company, "companyId" | "name">

interface CarrierPreferenceFormProps {
  tabType: CarrierTypeEnum
  control: Control<CarrierPreferencesData>
  watch: UseFormWatch<CarrierPreferencesData>
  companies: CompanyBase[]
  loadingCompanies: boolean
}

export const CarrierPreferenceForm: FC<CarrierPreferenceFormProps> = ({
  tabType,
  control,
  watch,
  companies,
  loadingCompanies,
}) => {
  const carrierId = watch(`${tabType}.carrierId`)
  const showAdditionalFields = Boolean(carrierId)

  const carrierOptions = companies.map((company) => ({
    label: company.name!,
    value: company.companyId,
  }))

  return (
    <Box className='flex flex-col gap-4'>
      <FormAutocomplete
        control={control}
        name={`${tabType}.carrierId`}
        label='Carriers'
        placeholder='Search and select a carrier'
        options={carrierOptions}
        disabled={loadingCompanies || carrierOptions.length === 0}
        data-testid='carrier-preference-carrier-select'
      />

      {showAdditionalFields && (
        <>
          <Box className='flex gap-4'>
            <FormTextField
              control={control}
              name={`${tabType}.accountNumber`}
              label='Acct #'
              data-testid='carrier-preference-account-input'
            />

            {tabType !== CarrierType.Sample && (
              <FormSelect
                control={control}
                name={`${tabType}.freightTerm`}
                label='Freight Terms'
                placeholder='Select...'
                options={FREIGHT_TERMS_OPTIONS}
                data-testid='carrier-preference-freight-terms-select'
              />
            )}
          </Box>

          <FormTextField
            control={control}
            name={`${tabType}.note`}
            label='BOL Note'
            data-testid='carrier-preference-bol-note-input'
          />

          <FormTextField
            control={control}
            name={`${tabType}.comment`}
            label='Internal Comment'
            multiline
            rows={4}
            data-testid='carrier-preference-comment-input'
          />

          {tabType === CarrierType.Sample && (
            <FormBooleanRadioGroup
              control={control}
              name={`${tabType}.customerPaysForSampleShipping`}
              label='Customer Pays for Sample Shipping'
              variant='yes-no'
              data-testid='carrier-preference-customer-pays-radio'
              sx={{
                "& .MuiFormControlLabel-label": { fontSize: "14px" },
              }}
            />
          )}
        </>
      )}
    </Box>
  )
}
